import { NetworkLog } from './types';
import { makeLogId, networkLoggerStore, truncateBody } from './networkLoggerStore';

let installed = false;

function safeStringifyBody(body: unknown): string | undefined {
    if (body == null) return undefined;
    try {
        if (typeof body === 'string') return truncateBody(body);
        if (body instanceof URLSearchParams) return truncateBody(body.toString());
        if (typeof FormData !== 'undefined' && body instanceof FormData) {
            const parts: string[] = [];
            body.forEach((value, key) => {
                if (typeof value === 'string') {
                    parts.push(`${key}=${value}`);
                } else {
                    parts.push(`${key}=[File ${value.name || 'unnamed'} ${value.size} bytes]`);
                }
            });
            return truncateBody(`[FormData]\n${parts.join('\n')}`);
        }
        if (typeof Blob !== 'undefined' && body instanceof Blob) {
            return `[Blob ${body.size} bytes${body.type ? ` ${body.type}` : ''}]`;
        }
        if (body instanceof ArrayBuffer) return `[ArrayBuffer ${body.byteLength} bytes]`;
        if (ArrayBuffer.isView(body)) return `[${body.constructor.name} ${body.byteLength} bytes]`;
        return truncateBody(JSON.stringify(body));
    } catch {
        return '[unserializable body]';
    }
}

function headersToRecord(headers: HeadersInit | Headers | undefined | null): Record<string, string> | undefined {
    if (!headers) return undefined;
    const record: Record<string, string> = {};
    try {
        if (typeof Headers !== 'undefined' && headers instanceof Headers) {
            headers.forEach((value, key) => {
                record[key] = value;
            });
        } else if (Array.isArray(headers)) {
            headers.forEach(([key, value]) => {
                record[key] = value;
            });
        } else {
            Object.entries(headers as Record<string, string>).forEach(([key, value]) => {
                record[key] = value;
            });
        }
    } catch {
        return undefined;
    }
    return Object.keys(record).length > 0 ? record : undefined;
}

function parseRawHeaders(raw: string): Record<string, string> | undefined {
    if (!raw) return undefined;
    const record: Record<string, string> = {};
    raw.split(/\r?\n/).forEach((line) => {
        const idx = line.indexOf(':');
        if (idx > 0) {
            record[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
        }
    });
    return Object.keys(record).length > 0 ? record : undefined;
}

function installFetchInterceptor(): void {
    const originalFetch = globalThis.fetch;

    globalThis.fetch = function loggedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        const id = makeLogId();
        const startedAt = Date.now();

        try {
            let url: string;
            let method = 'GET';
            let requestHeaders: Record<string, string> | undefined;

            if (typeof input === 'string') {
                url = input;
            } else if (input instanceof URL) {
                url = input.toString();
            } else {
                url = input.url;
                method = input.method || 'GET';
                requestHeaders = headersToRecord(input.headers);
            }
            if (init?.method) method = init.method;
            if (init?.headers) {
                requestHeaders = { ...(requestHeaders || {}), ...(headersToRecord(init.headers) || {}) };
            }

            networkLoggerStore.add({
                id,
                url,
                method: method.toUpperCase(),
                state: 'pending',
                source: 'fetch',
                startedAt,
                requestHeaders,
                requestBody: safeStringifyBody(init?.body),
            });
        } catch {
            // logging must never break the request
        }

        return originalFetch(input as RequestInfo, init).then(
            (response) => {
                const endedAt = Date.now();
                networkLoggerStore.update(id, {
                    state: response.ok ? 'success' : 'error',
                    status: response.status,
                    statusText: response.statusText,
                    endedAt,
                    durationMs: endedAt - startedAt,
                    responseHeaders: headersToRecord(response.headers),
                });
                try {
                    response
                        .clone()
                        .text()
                        .then(
                            (text) =>
                                networkLoggerStore.update(id, {
                                    responseBody: truncateBody(text),
                                    responseSize: text.length,
                                }),
                            () => undefined
                        );
                } catch {
                    // body capture is best-effort
                }
                return response;
            },
            (error) => {
                const endedAt = Date.now();
                networkLoggerStore.update(id, {
                    state: 'error',
                    endedAt,
                    durationMs: endedAt - startedAt,
                    error: error instanceof Error ? error.message : String(error),
                });
                throw error;
            }
        );
    };
}

interface XhrLogMeta {
    method: string;
    url: string;
    requestHeaders: Record<string, string>;
    errorKind?: string;
}

function installXhrInterceptor(): void {
    const proto = XMLHttpRequest.prototype;
    const originalOpen = proto.open;
    const originalSend = proto.send;
    const originalSetRequestHeader = proto.setRequestHeader;

    proto.open = function loggedOpen(
        this: XMLHttpRequest,
        method: string,
        url: string | URL,
        async?: boolean,
        username?: string | null,
        password?: string | null
    ) {
        (this as any).__networkLogMeta = {
            method: String(method).toUpperCase(),
            url: String(url),
            requestHeaders: {},
        } as XhrLogMeta;
        return originalOpen.call(this, method, url, async !== false, username, password);
    };

    proto.setRequestHeader = function loggedSetRequestHeader(this: XMLHttpRequest, name: string, value: string) {
        const meta = (this as any).__networkLogMeta as XhrLogMeta | undefined;
        if (meta) meta.requestHeaders[name] = value;
        return originalSetRequestHeader.call(this, name, value);
    };

    proto.send = function loggedSend(this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
        const meta = (this as any).__networkLogMeta as XhrLogMeta | undefined;
        if (meta) {
            const id = makeLogId();
            const startedAt = Date.now();

            try {
                const entry: NetworkLog = {
                    id,
                    url: meta.url,
                    method: meta.method,
                    state: 'pending',
                    source: 'xhr',
                    startedAt,
                    requestHeaders: Object.keys(meta.requestHeaders).length > 0 ? { ...meta.requestHeaders } : undefined,
                    requestBody: safeStringifyBody(body),
                };
                networkLoggerStore.add(entry);
            } catch {
                // logging must never break the request
            }

            this.addEventListener('error', () => {
                meta.errorKind = 'network error';
            });
            this.addEventListener('abort', () => {
                meta.errorKind = 'aborted';
            });
            this.addEventListener('timeout', () => {
                meta.errorKind = 'timeout';
            });
            // loadend fires last on every outcome (load, error, abort, timeout)
            this.addEventListener('loadend', () => {
                const endedAt = Date.now();
                if (this.status === 0) {
                    networkLoggerStore.update(id, {
                        state: 'error',
                        endedAt,
                        durationMs: endedAt - startedAt,
                        error: meta.errorKind || 'network error',
                    });
                    return;
                }
                let responseBody: string | undefined;
                try {
                    if (this.responseType === '' || this.responseType === 'text') {
                        responseBody = truncateBody(this.responseText);
                    } else {
                        responseBody = `[${this.responseType} response]`;
                    }
                } catch {
                    // responseText is unreadable for some responseTypes
                }
                networkLoggerStore.update(id, {
                    state: this.status >= 200 && this.status < 400 ? 'success' : 'error',
                    status: this.status,
                    statusText: this.statusText,
                    endedAt,
                    durationMs: endedAt - startedAt,
                    responseHeaders: parseRawHeaders(this.getAllResponseHeaders()),
                    responseBody,
                    responseSize: responseBody?.length,
                });
            });
        }
        return originalSend.call(this, body as any);
    };
}

export function installNetworkLogger(): void {
    if (installed) return;
    installed = true;
    installFetchInterceptor();
    installXhrInterceptor();
}
